import {useEffect, useMemo, useRef} from "react";
import axios from "axios";
import {useRefreshAccessToken} from "../hooks/useRefreshAccessToken";
import {useAuth} from "../hooks/useAuth";
import {BASE_URL} from "../utils/config";

export const useAxiosPrivate = () => {
    const refresh = useRefreshAccessToken();
    const {accessToken} = useAuth();
    const tokenRef = useRef(accessToken);

    // Update ref whenever accessToken changes
    useEffect(() => {
        tokenRef.current = accessToken;
    }, [accessToken]);

    const axiosPrivate = useMemo(() => {
        const instance = axios.create({
            baseURL: BASE_URL,
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true
        });

        // Set up interceptors immediately
        instance.interceptors.request.use(
            config => {
                // Always use the ref value for latest token
                config.headers['Authorization'] = `Bearer ${tokenRef.current}`;
                console.log("Request being sent with token:", tokenRef.current?.substring(0, 15));
                return config;
            },
            error => Promise.reject(error)
        );

        instance.interceptors.response.use(
            response => response,
            async (error) => {
                const prevRequest = error?.config;
                if (error?.response?.status === 403 && !prevRequest?.sent) {
                    prevRequest.sent = true;
                    const newAccessToken = await refresh();
                    tokenRef.current = newAccessToken; // Update the ref
                    prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return instance(prevRequest);
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, []);

    return axiosPrivate;
};
